import { AuthService } from '@/services/auth-service';
import { useAuthStore } from '@/store/use-auth-store';
import {
  registerSoulMapSessionRefresh,
  unregisterSoulMapSessionRefresh,
} from '@/utils/soulmap-session-refresh';
import {
  getEstablishedAuth0Sub,
  syncSessionFromBackend,
} from '@/utils/sync-auth-session';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

/** Registers SSO cookie refresh for Soul Map keepalive and axios 401 recovery. */
export function SoulMapSessionRefreshRegistration() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const authMethod = useAuthStore((state) => state.authMethod);
  const {
    isAuthenticated: isAuth0Authenticated,
    user: auth0User,
    getAccessTokenSilently,
    getIdTokenClaims,
  } = useAuth0();

  useEffect(() => {
    registerSoulMapSessionRefresh(async () => {
      if (
        !isAuthenticated ||
        authMethod !== 'sso' ||
        !isAuth0Authenticated
      ) {
        return false;
      }

      const establishedSub = getEstablishedAuth0Sub();
      if (
        establishedSub &&
        auth0User?.sub &&
        establishedSub !== auth0User.sub
      ) {
        return false;
      }

      try {
        const [accessToken, idToken] = await Promise.all([
          getAccessTokenSilently(),
          getIdTokenClaims(),
        ]);

        if (!idToken?.__raw) {
          return false;
        }

        await AuthService.establishSsoSession(accessToken, idToken.__raw);
        await syncSessionFromBackend({
          skipUnauthorizedRedirect: true,
          queryClient,
        });
        return true;
      } catch (error) {
        console.warn('Soul Map silent session refresh failed:', error);
        return false;
      }
    });

    return unregisterSoulMapSessionRefresh;
  }, [
    auth0User?.sub,
    authMethod,
    getAccessTokenSilently,
    getIdTokenClaims,
    isAuth0Authenticated,
    isAuthenticated,
    queryClient,
  ]);

  return null;
}
